/**
 * DashboardGrid - the Dashboard's widgets on a 12-column grid that each person
 * arranges (10/12/26). The layout model (order + sizes, flow, spacers,
 * narrower screens) is dashboardLayout.js; this draws it and edits it.
 *
 * Outside edit mode nothing moves: the widgets are plain grid cells. On a
 * narrower grid area the same order flows into 6 or 1 columns. A widget with
 * nothing to show (Recently viewed, before anything was opened) keeps its
 * place with a quiet line, so starting or ending edit mode never shifts the
 * page.
 *
 * "Customize dashboard" (a wide enough screen) opens edit mode on a draft:
 *   - drag a widget anywhere on it to move it: it lifts and follows the
 *     pointer, a dashed slot shows where it will land, the others slide to
 *     make room; near the top or bottom of the scrolling area the page
 *     scrolls by itself (autoScroll.js), so a widget can go from the bottom
 *     to the top without a mouse wheel; Escape puts it back
 *   - drag its corner to resize it: an outline follows the pointer, the
 *     widget snaps to whole cells within its own limits, its size badge
 *     counts along
 *   - keyboard, on a focused widget: arrows move it earlier / later,
 *     Shift + arrows resize it, Delete hides it
 *   - x hides a widget; "+ Add widget" lists the hidden ones and "Empty
 *     space" (a spacer: keeps a gap where you put it, invisible outside edit)
 *   - "Reset to default", Cancel, Save (saved to the person's account)
 * Widgets the person may not see (access) are never drawn, listed or moved;
 * they keep their place in the saved order.
 *
 * Smoothness (Sharif 10/12/26, "a little janky"): the lifted widget moves by
 * transform once a frame (rAF), React re-renders only when the drop place
 * changes, and the widgets' contents are memoized so a re-render does not
 * redraw them; the others slide (FLIP) from where they are ON SCREEN, so a
 * slide interrupted by the next one carries on instead of jumping. With
 * prefers-reduced-motion nothing slides or lifts.
 *
 *   <DashboardGrid layout={layout} onSave={save} widgets={{ id: { allowed,
 *     empty, emptyText, kind: 'kpi' | 'panel', render } }} />
 */
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../ui/Icon';
import {
  COLS,
  ROW_PX,
  GAP_PX,
  MAX_SPACERS,
  SPACER,
  cellAt,
  columnsFor,
  defaultLayout,
  dropIndex,
  fitToColumns,
  flow,
  hideWidget,
  isSpacer,
  mergeShown,
  moveTo,
  resizeItem,
  showWidget,
  sizeAt,
  titleOf,
  widgetOf,
} from './dashboardLayout';
import { scrollParentOf, scrollStep, viewportOf } from './autoScroll';

const THRESHOLD = 4;
const NO_DRAG = 'button, input, select, textarea, a, [data-no-drag]';
const SLIDE_MS = 200;
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
const raf = (fn) => (typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame(fn) : (fn(), 0));
const unraf = (id) => {
  if (id && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(id);
};
const reducedMotion = () => {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch {
    return false;
  }
};

/** The grid area's width (px), followed as it changes; 0 until measured. */
function useWidth(ref) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth || 0);
    if (typeof window.ResizeObserver !== 'function') return undefined;
    const ro = new window.ResizeObserver((entries) => {
      const w = entries[0] && entries[0].contentRect ? entries[0].contentRect.width : el.clientWidth;
      setWidth(Math.round(w || 0));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

/**
 * A widget's own content. Memoized: a drag re-renders the grid each time the
 * drop place changes, and redrawing every table and chart then is what made
 * it stutter. It redraws when the Dashboard hands it a new render (new data).
 */
const WidgetBody = memo(function WidgetBody({ render, empty, emptyText, editing }) {
  return (
    <div className="dash-w-body" inert={editing ? true : undefined}>
      {empty ? <div className="dash-w-empty">{emptyText}</div> : render()}
    </div>
  );
});

/** Six dots: "this can be picked up". */
const GrabDots = () => (
  <svg width="8" height="12" viewBox="0 0 8 12" aria-hidden="true" className="dash-w-dots">
    {[2, 6, 10].map((y) => [2, 6].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.1" />))}
  </svg>
);

/** A small picture of a widget's default size on the 12-column grid (the tray). */
const SizePreview = ({ w, h }) => (
  <span className="dash-tray-preview" aria-hidden="true">
    <span style={{ width: `${(w / COLS) * 100}%`, height: `${(Math.min(h, 6) / 6) * 100}%` }} />
  </span>
);

export default function DashboardGrid({ layout, widgets, onSave }) {
  const gridRef = useRef(null);
  const width = useWidth(gridRef);
  const cols = columnsFor(width);
  const [draft, setDraft] = useState(null); // the layout being edited, or null
  const editing = !!draft;
  const [preview, setPreview] = useState(null); // shown items while a drag is under way
  const [active, setActive] = useState(null); // { id, mode: 'move' | 'resize', box }
  const [trayOpen, setTrayOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [said, setSaid] = useState(''); // the live region's last words
  const focusAfter = useRef(null);
  const els = useRef(new Map()); // id -> the widget's element
  const before = useRef(null); // id -> { left, top } on screen before a change (FLIP)
  const slides = useRef(new Map()); // id -> its running slide (Animation)
  const floatRef = useRef(null); // the lifted widget, moved per frame
  const shift = useRef({ x: 0, y: 0 }); // and how far it is from its box (px)
  const ghostRef = useRef(null); // the resize outline
  const ghost = useRef(null); // and its size ({ width, height } px)
  const trayRef = useRef(null);

  const allowed = useCallback((id) => isSpacer(id) || !!(widgets[id] && widgets[id].allowed), [widgets]);
  const current = draft || layout;
  const shown = useMemo(() => current.items.filter((i) => allowed(i.id)), [current, allowed]);
  const items = preview || shown;
  const cells = useMemo(() => flow(fitToColumns(items, cols), cols), [items, cols]);
  const canEdit = cols === COLS;

  /* ── FLIP: the others slide from where they were on screen ────────── */
  /** Remember where every widget is drawn now; the next render slides them from there. */
  const remember = useCallback(() => {
    const grid = gridRef.current;
    if (!grid || reducedMotion()) return;
    const g = grid.getBoundingClientRect();
    const map = new Map();
    els.current.forEach((el, id) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      map.set(id, { left: r.left - g.left, top: r.top - g.top });
    });
    before.current = map;
  }, []);

  useLayoutEffect(() => {
    const was = before.current;
    if (!was) return;
    before.current = null;
    const grid = gridRef.current;
    if (!grid) return;
    const g = grid.getBoundingClientRect();
    els.current.forEach((el, id) => {
      if (!el || typeof el.animate !== 'function') return;
      if (active && active.mode === 'move' && active.id === id) return; // the lifted one follows the pointer
      const from = was.get(id);
      if (!from) return;
      const running = slides.current.get(id);
      if (running) {
        running.cancel();
        slides.current.delete(id);
      }
      const r = el.getBoundingClientRect();
      const dx = from.left - (r.left - g.left);
      const dy = from.top - (r.top - g.top);
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
      const settling = el.dataset.settle === '1';
      delete el.dataset.settle;
      const a = el.animate(
        settling
          ? [
              { transform: `translate(${dx}px, ${dy}px) scale(1.02)`, boxShadow: '0 22px 48px rgba(0,0,0,0.2)' },
              { transform: 'none', boxShadow: '0 0 0 rgba(0,0,0,0)' },
            ]
          : [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
        { duration: settling ? 240 : SLIDE_MS, easing: EASE }
      );
      slides.current.set(id, a);
      a.onfinish = () => {
        if (slides.current.get(id) === a) slides.current.delete(id);
      };
    });
  });

  // keyboard edits keep the focus on the widget that moved
  useEffect(() => {
    const id = focusAfter.current;
    if (!id) return;
    focusAfter.current = null;
    const el = els.current.get(id);
    const handle = el && el.querySelector('[data-move-handle]');
    if (handle) handle.focus({ preventScroll: false });
  });

  // the Add-widget tray closes on a click elsewhere or Escape
  useEffect(() => {
    if (!trayOpen) return undefined;
    const away = (e) => {
      if (trayRef.current && !trayRef.current.contains(e.target)) setTrayOpen(false);
    };
    const key = (e) => {
      if (e.key === 'Escape') setTrayOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [trayOpen]);

  /* ── edit mode ─────────────────────────────────────────────────── */
  const start = () => {
    setDraft({ ...layout, items: [...layout.items], hidden: [...layout.hidden] });
    setError(null);
    setTrayOpen(false);
  };
  const cancel = () => {
    remember();
    setDraft(null);
    setPreview(null);
    setActive(null);
    setTrayOpen(false);
    setError(null);
  };
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setDraft(null);
      setTrayOpen(false);
    } catch {
      setError('Could not save the layout - try again.');
    } finally {
      setSaving(false);
    }
  };
  /** Apply an edit made to the SHOWN items. */
  const commit = (editedShown) => setDraft((d) => ({ ...d, items: mergeShown(d.items, allowed, editedShown) }));
  const hide = (id) => {
    remember();
    setDraft((d) => hideWidget(d, id));
    setSaid(`${titleOf(id)} ${isSpacer(id) ? 'removed' : 'hidden'}`);
  };
  const add = (id) => {
    remember();
    setDraft((d) => showWidget(d, id));
    setTrayOpen(false);
    setSaid(`${id === 'spacer' ? 'Empty space' : titleOf(id)} added at the end`);
  };
  const reset = () => {
    remember();
    setDraft(defaultLayout());
    setSaid('Back to the default layout (not saved yet)');
  };
  const hiddenAddable = editing ? draft.hidden.filter((id) => allowed(id)) : [];
  const spacers = editing ? draft.items.filter((i) => isSpacer(i.id)).length : 0;

  /* ── pointer: move and resize ──────────────────────────────────── */
  const onPointerDown = (e, id, mode) => {
    if (!canEdit || (e.button != null && e.button !== 0)) return;
    if (mode === 'move' && e.target.closest(NO_DRAG)) return;
    const grid = gridRef.current;
    const el = els.current.get(id);
    if (!grid || !el) return;
    e.preventDefault();
    const sx = e.clientX;
    const sy = e.clientY;
    const startList = shown;
    const box = { left: el.offsetLeft, top: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight };
    const g0 = grid.getBoundingClientRect();
    const grab = { dx: sx - g0.left - box.left, dy: sy - g0.top - box.top };
    // the resize handle sits a little inside the corner: keep that offset so the outline starts at the box
    const inset = (v) => Math.max(0, Math.min(24, v));
    const corner = { dx: inset(box.left + box.width - (sx - g0.left)), dy: inset(box.top + box.height - (sy - g0.top)) };
    const W = width || grid.clientWidth;
    const scroller = scrollParentOf(grid);
    const root = document.documentElement;
    let list = startList;
    let started = false;
    let done = false;
    let frame = 0;
    let scrollFrame = 0;
    let last = { x: sx, y: sy };

    /** Follow the pointer: the lifted widget / the outline, and the drop place or snapped size. */
    const paint = () => {
      frame = 0;
      if (done) return;
      const g = grid.getBoundingClientRect();
      const px = last.x - g.left;
      const py = last.y - g.top;
      if (mode === 'move') {
        shift.current = { x: px - grab.dx - box.left, y: py - grab.dy - box.top };
        if (floatRef.current) floatRef.current.style.transform = `translate3d(${shift.current.x}px, ${shift.current.y}px, 0)`;
        const now = flow(list, COLS);
        const mine = now.find((c) => c.id === id);
        const { cx, cy } = cellAt(px, py, W);
        if (mine && cx >= mine.x && cx < mine.x + mine.w && cy >= mine.y && cy < mine.y + mine.h) return;
        const others = now.filter((c) => c.id !== id);
        const to = dropIndex(others, cx, cy);
        const next = moveTo(list, id, to);
        if (next.some((it, i) => it.id !== list[i].id)) {
          remember();
          list = next;
          setPreview(next);
        }
      } else {
        ghost.current = { width: Math.max(40, px + corner.dx - box.left), height: Math.max(40, py + corner.dy - box.top) };
        if (ghostRef.current) {
          ghostRef.current.style.width = `${ghost.current.width}px`;
          ghostRef.current.style.height = `${ghost.current.height}px`;
        }
        const { w, h } = sizeAt(px + corner.dx, py + corner.dy, box.left, box.top, W);
        const next = resizeItem(list, id, w, h);
        const was = list.find((i) => i.id === id);
        const now = next.find((i) => i.id === id);
        if (was.w !== now.w || was.h !== now.h) {
          remember();
          list = next;
          setPreview(next);
        }
      }
    };
    const schedule = () => {
      if (!frame && !done) frame = raf(paint);
    };

    /** Near the top or bottom edge, scroll the page area (faster the closer), and follow it. */
    const autoScroll = () => {
      scrollFrame = 0;
      if (done || !started) return;
      const vp = viewportOf(scroller);
      const step = scrollStep(last.y, vp.top, vp.bottom);
      if (!step) return;
      const was = scroller.scrollTop;
      scroller.scrollTop = was + step;
      if (scroller.scrollTop !== was) paint();
      scrollFrame = raf(autoScroll);
    };
    // the wheel, a scrollbar or the auto-scroll: the drop place follows what is under the pointer now
    const onScroll = () => {
      if (started) schedule();
    };

    const finish = (commitIt) => {
      done = true;
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      document.removeEventListener('keydown', key, true);
      document.removeEventListener('scroll', onScroll, true);
      unraf(frame);
      unraf(scrollFrame);
      if (!started) return;
      root.classList.remove('dash-dragging', 'dash-resizing');
      document.body.style.userSelect = '';
      if (commitIt) {
        commit(list);
        const it = list.find((i) => i.id === id);
        setSaid(
          mode === 'move'
            ? `${titleOf(id)} moved to place ${list.findIndex((i) => i.id === id) + 1}`
            : `${titleOf(id)} is now ${it.w} wide by ${it.h} high`
        );
      } else {
        setSaid(`${mode === 'move' ? 'Move' : 'Resize'} cancelled`);
      }
      // the lifted widget settles from where it was let go, the others slide back if cancelled
      remember();
      if (mode === 'move' && els.current.get(id)) els.current.get(id).dataset.settle = '1';
      shift.current = { x: 0, y: 0 };
      ghost.current = null;
      setPreview(null);
      setActive(null);
    };
    function move(ev) {
      last = { x: ev.clientX, y: ev.clientY };
      if (!started) {
        if (Math.abs(last.x - sx) < THRESHOLD && Math.abs(last.y - sy) < THRESHOLD) return;
        started = true;
        document.body.style.userSelect = 'none';
        root.classList.add(mode === 'move' ? 'dash-dragging' : 'dash-resizing');
        shift.current = { x: 0, y: 0 };
        ghost.current = { width: box.width, height: box.height };
        setTrayOpen(false);
        setPreview(startList);
        setActive({ id, mode, box });
      }
      if (ev.cancelable) ev.preventDefault();
      schedule();
      if (!scrollFrame) scrollFrame = raf(autoScroll);
    }
    function up(ev) {
      if (!started) {
        finish(false);
        return;
      }
      // a drag is not a click: swallow the click that follows it
      const swallow = (c) => {
        c.stopPropagation();
        c.preventDefault();
      };
      window.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
      if (ev.type === 'pointerup') {
        last = { x: ev.clientX, y: ev.clientY };
        unraf(frame);
        paint();
      }
      finish(ev.type === 'pointerup');
    }
    // Escape puts it back where it was
    function key(ev) {
      if (ev.key !== 'Escape' || !started) return;
      ev.preventDefault();
      ev.stopPropagation();
      list = startList;
      finish(false);
    }
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
    document.addEventListener('keydown', key, true);
    document.addEventListener('scroll', onScroll, true);
  };

  /* ── keyboard ──────────────────────────────────────────────────── */
  const onKeyDown = (e, id) => {
    if (!canEdit) return;
    const i = shown.findIndex((it) => it.id === id);
    const it = shown[i];
    let next = null;
    let words = '';
    if (e.shiftKey && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      const w = it.w + (e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0);
      const h = it.h + (e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0);
      next = resizeItem(shown, id, w, h);
      const now = next[i];
      words = `${titleOf(id)} is now ${now.w} wide by ${now.h} high`;
    } else if (['ArrowLeft', 'ArrowUp'].includes(e.key)) {
      next = moveTo(shown, id, i - 1);
      words = `${titleOf(id)} moved to place ${Math.max(1, i)}`;
    } else if (['ArrowRight', 'ArrowDown'].includes(e.key)) {
      next = moveTo(shown, id, i + 1);
      words = `${titleOf(id)} moved to place ${Math.min(shown.length, i + 2)}`;
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      hide(id);
      return;
    } else return;
    e.preventDefault();
    remember();
    focusAfter.current = id;
    commit(next);
    setSaid(words);
  };

  /* ── drawing ───────────────────────────────────────────────────── */
  const setEl = (id) => (el) => {
    if (el) els.current.set(id, el);
    else els.current.delete(id);
  };
  const lifted = active && active.mode === 'move' ? active : null;
  const resizing = active && active.mode === 'resize' ? active : null;
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    gridAutoRows: `${ROW_PX}px`,
    gap: GAP_PX,
    position: 'relative',
    marginBottom: 16,
  };
  const hint = canEdit
    ? 'Drag to move (the page scrolls at its edges) · drag a corner to resize · Esc cancels · keys: arrows, Shift + arrows'
    : 'Widen the window to move or resize widgets.';

  return (
    <div className={editing ? 'dash dash-editing' : 'dash'}>
      {/* one height in both modes, so entering edit mode never shifts the widgets */}
      <div className="dash-toolbar">
        {editing ? (
          <>
            <div className="dash-hint" title={hint}>
              <span className="dash-hint-tag">
                <Icon name="dashboard" size={11} color="#1B5E20" /> Customizing
              </span>
              <span className="dash-hint-text">{hint}</span>
            </div>
            <div className="dash-actions">
              <div ref={trayRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="dash-btn"
                  aria-expanded={trayOpen}
                  aria-haspopup="menu"
                  onClick={() => setTrayOpen((v) => !v)}
                >
                  <Icon name="plus" size={12} /> Add widget
                  {hiddenAddable.length > 0 && <span className="dash-btn-count">{hiddenAddable.length}</span>}
                </button>
                {trayOpen && (
                  <div role="menu" aria-label="Add a widget" className="dash-tray go-drop-in">
                    <div className="dash-tray-head">Hidden widgets</div>
                    {hiddenAddable.length === 0 && <div className="dash-tray-none">Every widget is on the dashboard.</div>}
                    {hiddenAddable.map((id) => {
                      const L = widgetOf(id);
                      return (
                        <button
                          key={id}
                          type="button"
                          role="menuitem"
                          aria-label={`Add ${titleOf(id)}`}
                          onClick={() => add(id)}
                          className="dash-tray-item"
                        >
                          <SizePreview w={L.w} h={L.h} />
                          <span className="dash-tray-name">{titleOf(id)}</span>
                          <span className="dash-tray-size">
                            {L.w} × {L.h}
                          </span>
                        </button>
                      );
                    })}
                    <div className="dash-tray-head">Layout</div>
                    <button
                      type="button"
                      role="menuitem"
                      aria-label="Add Empty space"
                      disabled={spacers >= MAX_SPACERS}
                      onClick={() => add('spacer')}
                      className="dash-tray-item"
                    >
                      <span className="dash-tray-preview dash-tray-preview-gap" aria-hidden="true">
                        <span style={{ width: `${(SPACER.w / COLS) * 100}%`, height: '66%' }} />
                      </span>
                      <span className="dash-tray-name">
                        Empty space <span className="dash-tray-note">keeps a gap</span>
                      </span>
                      <span className="dash-tray-size">
                        {SPACER.w} × {SPACER.h}
                      </span>
                    </button>
                    <div className="dash-tray-foot">Added at the end - drag it where you want it.</div>
                  </div>
                )}
              </div>
              <button type="button" className="dash-btn" onClick={reset}>
                Reset to default
              </button>
              <span className="dash-actions-sep" aria-hidden="true" />
              <button type="button" className="dash-btn" onClick={cancel}>
                Cancel
              </button>
              <button type="button" className="dash-btn dash-btn-primary" disabled={saving} onClick={save}>
                <Icon name="check" size={12} color="#fff" /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        ) : (
          <div className="dash-actions">
            <button
              type="button"
              className="dash-btn"
              disabled={!canEdit}
              onClick={start}
              title={canEdit ? 'Move, resize, hide and add widgets - saved to your account' : 'Widen the window to customize the dashboard'}
            >
              <Icon name="dashboard" size={12} /> Customize dashboard
            </button>
          </div>
        )}
      </div>
      {error && (
        <div role="alert" className="dash-error">
          {error}
        </div>
      )}
      <div
        aria-live="polite"
        className="sr-only"
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
      >
        {said}
      </div>

      <div
        ref={gridRef}
        data-dash-grid=""
        data-cols={cols}
        className={editing ? 'dash-grid dash-grid-editing' : 'dash-grid'}
        style={gridStyle}
      >
        {cells.flatMap((c) => {
          const id = c.id;
          const spacer = isSpacer(id);
          const W = widgets[id];
          const isLifted = lifted && lifted.id === id;
          const isResizing = resizing && resizing.id === id;
          const place = { gridColumn: `${c.x + 1} / span ${c.w}`, gridRow: `${c.y + 1} / span ${c.h}` };
          const style = isLifted
            ? {
                position: 'absolute',
                left: lifted.box.left,
                top: lifted.box.top,
                width: lifted.box.width,
                height: lifted.box.height,
                zIndex: 40,
                transform: `translate3d(${shift.current.x}px, ${shift.current.y}px, 0)`,
              }
            : { ...place, position: 'relative', minWidth: 0, minHeight: 0 };
          const kind = spacer ? 'spacer' : W.kind || 'panel';
          const name = spacer ? 'Empty space' : titleOf(id);
          return [
            isLifted && <div key="__slot" className="dash-slot" aria-hidden="true" style={place} />,
            isResizing && (
              <div
                key="__ghost"
                ref={ghostRef}
                className="dash-resize-ghost"
                aria-hidden="true"
                style={{
                  left: resizing.box.left,
                  top: resizing.box.top,
                  width: (ghost.current || resizing.box).width,
                  height: (ghost.current || resizing.box).height,
                }}
              />
            ),
            <div
              key={id}
              ref={(el) => {
                setEl(id)(el);
                if (isLifted) floatRef.current = el;
              }}
              data-widget={id}
              data-kind={kind}
              className={`dash-w${editing ? ' dash-w-edit' : ''}${isLifted ? ' dash-w-lifted' : ''}${isResizing ? ' dash-w-resizing' : ''}`}
              style={style}
              aria-hidden={spacer && !editing ? 'true' : undefined}
            >
              {!spacer && (
                <WidgetBody
                  render={W.render}
                  empty={!!W.empty}
                  emptyText={W.emptyText || `${titleOf(id)}: nothing to show yet`}
                  editing={editing}
                />
              )}
              {editing && (
                <div
                  data-move-handle=""
                  role="button"
                  tabIndex={0}
                  className="dash-w-overlay"
                  aria-label={`${name}, ${c.w} wide by ${c.h} high. Drag or use the arrow keys to move, Shift + arrows to resize, Delete to ${spacer ? 'remove' : 'hide'}.`}
                  onPointerDown={(e) => onPointerDown(e, id, 'move')}
                  onKeyDown={(e) => onKeyDown(e, id)}
                >
                  <div className="dash-w-chip">
                    <GrabDots />
                    <span className="dash-w-title">{name}</span>
                    <span className="dash-w-size">
                      {c.w} × {c.h}
                    </span>
                    <button
                      type="button"
                      className="dash-w-x"
                      aria-label={`${spacer ? 'Remove' : 'Hide'} ${name}`}
                      title={spacer ? 'Remove this empty space' : 'Hide - bring it back with "+ Add widget"'}
                      onClick={() => hide(id)}
                    >
                      <Icon name="x" size={10} />
                    </button>
                  </div>
                  {canEdit && (
                    <span
                      data-resize-handle=""
                      data-no-drag=""
                      className="dash-w-grip"
                      aria-hidden="true"
                      title={`Drag to resize (${widgetOf(id).minW}-${widgetOf(id).maxW} wide, ${widgetOf(id).minH}-${widgetOf(id).maxH} high)`}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        onPointerDown(e, id, 'resize');
                      }}
                    >
                      <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                        <path d="M9 3L3 9M9 6.5L6.5 9" />
                      </svg>
                    </span>
                  )}
                </div>
              )}
            </div>,
          ].filter(Boolean);
        })}
      </div>
    </div>
  );
}
